import React, { useState, useEffect } from "react";

import { css } from "@emotion/css";
import { CellContext } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import { FontSizes } from "@/assets/fonts.ts";
import { Icons } from "@/assets/icons.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import type { GroupedInclusionDraft } from "@/models/GroupedInclusionModel.ts";
import { Queries } from "@/queries/Queries.ts";
import type { Theme } from "@/theme/CreateTheme.ts";
import { Button } from "@/uiKit/atoms/Button.tsx";
import { TextInput } from "@/uiKit/atoms/inputs/TextInput.tsx";
import { TextToast } from "@/uiKit/atoms/Toast.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";
import { ColumnHeader } from "@/uiKit/organisms/PatientSync/columnHeader.tsx";
import { cz } from "@/utils/zodUtils.ts";

const phoneSchema = cz.phone();

export const PhoneCell: React.FC<CellContext<GroupedInclusionDraft, string | undefined>> = ({ row, cell }) => {
  const [value, setValue] = useState<string | undefined>(row.original.phoneNumber);
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();
  const savePhone = Queries.patientSync.useSetGroupedInclusionPhoneNumber();
  const state = cell.getCellEditionState?.();
  useEffect(() => {
    if (state === "unset") {
      if (value) {
        cell.setCellViewing?.();
      } else {
        cell.setCellEditing?.();
      }
    }
  }, [state, value, cell]);

  if (state === "editing" || state === "adding") {
    return (
      <div className={styles.phoneCell}>
        <TextInput
          id={`sync-ongoing-import-phone-${row.original.id}`}
          value={value ?? ""}
          placeholder={"+33 __ __ __ __ __"}
          onChange={v => setValue(v)}
        />
        <div className={styles.phoneActions}>
          <Button
            variant="text"
            data-action="sync.ongoing-import.save"
            startIcon={<Icons.disk className={styles.icon} />}
            onClick={() => {
              const parsed = phoneSchema.safeParse(value);
              if (!parsed.success) {
                TextToast.error(t("errors.invalidPhoneNumber"));
              } else {
                savePhone.mutate(
                  { id: row.original.id, phoneNumber: parsed.data },
                  {
                    onSuccess: () => {
                      cell.setCellViewing?.();
                      TextToast.success(t("patientSync.ongoingInclusionTable.phoneSaved"));
                    },
                    onError: () => {
                      TextToast.error(t("errors.unknownError"));
                    },
                  },
                );
              }
            }}
          />
        </div>
      </div>
    );
  } else if (state === "viewing") {
    return (
      <div className={styles.phoneCell}>
        <Typography variant="body">{row.original.phoneNumber}</Typography>
        <Button
          variant="text"
          data-action="sync.ongoing-import.edit-phone"
          startIcon={<Icons.edit className={styles.icon} />}
          onClick={() => cell.setCellEditing?.()}
        />
      </div>
    );
  }
  return null;
};

export const PhoneHeader: React.FC = () => <ColumnHeader translationKey="patientSync.ongoingInclusionTable.phone" />;

const makeStyles = (theme: Theme) => ({
  phoneCell: css({
    display: "flex",
    gap: theme.spacing(8),
    alignItems: "center",
  }),
  phoneActions: css({
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
  }),
  icon: css({
    width: FontSizes.body,
    height: FontSizes.body,
  }),
});
